import { LoadingState } from "@repo/app-types/client/client";
import type { JobType } from "@repo/app-types/entities/jobType";
import { buildProjectUrlPath } from "@repo/app-types/helpers/adminAppRoutes";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { LoadingText } from "@repo/ui/Buttons/loadingText";
import { Input } from "@repo/ui/Inputs/input";
import { Modal } from "@repo/ui/Modal/modal";
import { ModalHeader } from "@repo/ui/Modal/modalHeader";
import { type FC, useState } from "react";
import { useNavigate } from "react-router";
import { useAppDispatch, useAppSelector } from "../../../hooks/hook.js";
import { JobTypePicker } from "../../jobTypes/jobTypePicker.js";
import {
  NotificationType,
  showNotification,
} from "../../notificationsOverlay/notificationsSlice.js";
import { PhoneNumberSelect } from "../../phoneNumbers/phoneNumberSelect.js";
import { selectAllPhoneNumbers } from "../../phoneNumbers/phoneNumbersSlice.js";
import { selectTeammate } from "../../teammate/teammateSlice.js";
import {
  JobDescriptionTextArea,
  MAX_JD_LENGTH,
  MIN_JD_LENGTH,
} from "../projectSettings/jobDescriptionSetting.js";
import { JobOpeningDetailsInputs } from "../projectSettings/recruiting/jobOpeningSettings.js";
import { createRecruitingProject } from "../projectsSlice.js";

export interface CreateRecruitingProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const CreateRecruitingProjectModal: FC<
  CreateRecruitingProjectModalProps
> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const teammate = useAppSelector(selectTeammate);
  if (!teammate) {
    throw new Error("Teammate not found");
  }

  const [jobType, setJobType] = useState<JobType | null>(null);
  const [jobOpeningName, setJobOpeningName] = useState("");
  const [jobDescription, setJobDescription] = useState("");
  const [selectedPhoneNumberId, setSelectedPhoneNumberId] = useState<string>();
  const [location, setLocation] = useState("");
  const [pay, setPay] = useState("");
  const [hours, setHours] = useState("");
  const phoneNumbers = useAppSelector(selectAllPhoneNumbers);

  const [createProjectStatus, setCreateProjectStatus] = useState<LoadingState>(
    LoadingState.LOADED,
  );

  const handleLocationBlur = () => {
    if (jobType && location && !jobOpeningName) {
      setJobOpeningName(`${jobType.name} - ${location}`);
    }
  };

  const onClickCreate = async () => {
    setCreateProjectStatus(LoadingState.LOADING);

    try {
      const newProject = await dispatch(
        createRecruitingProject({
          name: jobOpeningName,
          jobDescription: jobDescription || undefined,
          phoneNumberId: selectedPhoneNumberId,
          jobTypeId: jobType?.id ?? undefined,
          location: location ? { description: location } : undefined,
          pay: pay ? { description: pay } : undefined,
          hours: hours ? { description: hours } : undefined,
        }),
      ).unwrap();

      const newProjectId = newProject.id;

      showNotification(dispatch, {
        id: `project-created-${new Date().getTime().toString()}`,
        primaryMessage: `Job opening successfully created`,
        type: NotificationType.SUCCESS,
      });

      setCreateProjectStatus(LoadingState.LOADED);
      navigate(buildProjectUrlPath(newProjectId));
    } catch (err) {
      console.error("error creating project", err);
      setCreateProjectStatus(LoadingState.ERROR);
    }
  };

  return (
    <Modal isOpen={isOpen} onCancel={onClose}>
      <ModalHeader>Create job opening</ModalHeader>
      <div className="mt-4 flex flex-col space-y-3">
        <div>
          <div className="w-[300px]">
            <JobTypePicker
              jobTypeId={jobType?.id ?? null}
              onChange={setJobType}
            />
          </div>
          <div className="mt-2 text-sm text-muted-foreground">
            (Optional) Choose an existing job type to share interview questions
            and criteria across multiple job openings.
          </div>
        </div>
        {jobType ? (
          <JobOpeningDetailsInputs
            location={location}
            pay={pay}
            hours={hours}
            onLocationChange={setLocation}
            onPayChange={setPay}
            onHoursChange={setHours}
            onLocationBlur={handleLocationBlur}
          />
        ) : (
          <div>
            <JobDescriptionTextArea
              description="Paste a job description that describes the key responsibilities and requirements (no need to worry about text formatting)."
              onChange={setJobDescription}
              value={jobDescription}
              height="150px"
            />
          </div>
        )}
        {phoneNumbers.length > 1 ? (
          <div>
            <PhoneNumberSelect
              onChange={setSelectedPhoneNumberId}
              phoneNumberId={selectedPhoneNumberId}
            />
          </div>
        ) : null}
        <Input
          autoFocus
          label="Job opening name"
          onChange={(evt) => {
            setJobOpeningName(evt.target.value);
          }}
          placeholder="Store Manager - San Francisco"
          required={Boolean(jobType)}
          value={jobOpeningName}
          maxLength={100}
        />
      </div>
      <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
        <div className="ml-3">
          <Button
            isDisabled={
              !jobOpeningName ||
              (!jobType &&
                (!jobDescription ||
                  jobDescription.length < MIN_JD_LENGTH ||
                  jobDescription.length > MAX_JD_LENGTH)) ||
              createProjectStatus === LoadingState.LOADING
            }
            isLoading={createProjectStatus === LoadingState.LOADING}
            onClick={() => void onClickCreate()}
            variant={ButtonVariant.Primary}
          >
            <LoadingText
              isLoading={
                !!jobDescription && createProjectStatus === LoadingState.LOADING
              }
              stages={[
                "Analyzing job description...",
                "Generating interview questions...",
                "Generating job qualifications...",
                "Improving interview depth...",
                "Creating job opening...",
              ]}
            >
              Create job opening
            </LoadingText>
          </Button>
        </div>
        <Button onClick={onClose} variant={ButtonVariant.Secondary}>
          Cancel
        </Button>
      </div>
    </Modal>
  );
};
